import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, ShoppingCart, X, ZoomIn, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackageDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [settings, setSettings] = useState({ ppn_enabled: false, ppn_rate: 1.0 });

  useEffect(() => {
    const fetchPkg = async () => {
      try {
        const res = await axios.get(`${API}/packages/${id}`);
        setPkg(res.data);
      } catch (err) {
        toast.error('Paket tidak ditemukan');
        navigate('/packages');
      } finally {
        setLoading(false);
      }
    };
    fetchPkg();
  }, [id, navigate]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Silakan login dulu');
      navigate('/login');
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/cart/add`, { package_id: id, quantity });
      toast.success('Paket ditambahkan ke keranjang!');
      navigate('/cart');
    } catch (err) {
      toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!pkg) return null;

  const fmt = (p) => `Rp ${(p || 0).toLocaleString('id-ID')}`;
  const images = pkg.images || [];

  return (
    <div className="min-h-screen py-12 bg-background">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate('/packages')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Kembali
        </Button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold font-['Outfit'] mb-2">{pkg.package_name}</h1>
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="secondary">{pkg.package_type}</Badge>
            <Badge variant="outline">{pkg.duration_days} Hari</Badge>
            {pkg.is_active && <Badge className="bg-green-100 text-green-800">Tersedia</Badge>}
          </div>
          <p className="text-2xl font-bold text-primary">{fmt(pkg.price)} <span className="text-sm font-normal text-muted-foreground">/ orang</span></p>
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Kuota: {pkg.available_quota}/{pkg.quota} tersedia</span>
            {pkg.quota > 0 && (
              <div className="flex-1 max-w-xs h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((pkg.quota - (pkg.available_quota || 0)) / pkg.quota) * 100)}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Gambar utama + gallery */}
        {images.length > 0 && (
          <div className="mb-8">
            {/* Main image — contain (full fit) + overlay kontak WA */}
            <div
              className="rounded-2xl overflow-hidden border border-border bg-gray-100 dark:bg-gray-800 aspect-video mb-3 relative group cursor-pointer"
              onClick={() => setZoomImage(selectedImage || images[0])}
            >
              <img
                src={selectedImage || images[0]}
                alt={pkg.package_name}
                className="w-full h-full object-contain bg-gray-100 dark:bg-gray-800"
              />
              {/* Bar memanjang penutup nomor HP di bawah gambar */}
              <div className="absolute inset-x-0 bottom-0 h-10 bg-gray-100 dark:bg-gray-800 border-t border-border" />
              {/* Layer kontak WhatsApp di depan gambar */}
              <a
                href="https://wa.me/6285700040403"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-3 right-3 flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium px-3 py-2 rounded-full shadow-lg transition-colors z-10"
              >
                <MessageCircle className="h-4 w-4" />
                Hubungi WA
              </a>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
                <ZoomIn className="h-8 w-8 text-white/0 group-hover:text-white/80 transition-all" />
              </div>
            </div>

            {/* Thumbnail gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(img)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors bg-gray-100 dark:bg-gray-800 ${
                      (selectedImage || images[0]) === img ? 'border-primary' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={img} alt={`Gambar ${i + 1}`} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Deskripsi */}
        <Card className="rounded-2xl mb-8">
          <CardHeader>
            <CardTitle>Deskripsi</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {pkg.description || 'Deskripsi paket belum tersedia.'}
            </p>
          </CardContent>
        </Card>

        {/* Termasuk */}
        {pkg.includes && pkg.includes.length > 0 && (
          <Card className="rounded-2xl mb-8">
            <CardHeader>
              <CardTitle>Fasilitas Termasuk</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {pkg.includes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-green-500 mt-0.5">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Tidak Termasuk */}
        {pkg.excludes && pkg.excludes.length > 0 && (
          <Card className="rounded-2xl mb-8">
            <CardHeader>
              <CardTitle>Tidak Termasuk</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {pkg.excludes.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-red-400 mt-0.5">✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Aksi */}
        <div className="bg-card border border-border rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-xl font-bold">{fmt(pkg.price * quantity)}</p>
            {settings.ppn_enabled && (
              <p className="text-sm text-muted-foreground">*Harga belum termasuk PPN ({settings.ppn_rate}%)</p>
            )}
            </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</Button>
              <span className="w-10 text-center font-medium">{quantity}</span>
              <Button variant="outline" size="sm" onClick={() => setQuantity(quantity + 1)}>+</Button>
            </div>
            <Button className="rounded-full" onClick={handleAddToCart} disabled={adding}>
              {adding ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <ShoppingCart className="h-5 w-5 mr-2" />}
              Tambah ke Keranjang
            </Button>
          </div>
        </div>
      </div>

      {/* Zoom Modal — fullscreen + bar penutup */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setZoomImage(null)}
        >
          <button
            onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={zoomImage}
              alt="Zoom"
              className="w-full h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Bar penutup nomor HP di modal zoom */}
            <div className="absolute inset-x-0 bottom-0 h-16 bg-black/90" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageDetailPage;
